import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

const OptionsDisclaimerTable = ({ loading, disclaimers, fetchDisclaimers }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState(null);

  const handleEdit = (item) => {
    setEditData(item);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditData(null);
  };

  const handleSave = async () => {
    try {
      await axiosConfig({
        method: "put",
        url: `/disclaimers/${editData.id}/`,
        data: {
          disclaimer_text: editData.disclaimer_text,
          is_active: editData.is_active,
        },
      });
      toast.success("Disclaimer updated successfully");
      fetchDisclaimers();
      handleCloseDialog();
    } catch (error) {
      console.error("Error updating disclaimer:", error);
      toast.error("Failed to update disclaimer");
    }
  };

  const handleDelete = async (id) => {
    try {
    const res =   await axiosConfig({
        method: "delete",
        url: `/disclaimers/${id}/`,
      });
      toast.success("Disclaimer deleted successfully");
      fetchDisclaimers();
    } catch (error) {
      console.error("Error deleting disclaimer:", error);
      toast.error("Failed to delete disclaimer");
    }
  };

  const handleChangeText = (e) =>
    setEditData({ ...editData, disclaimer_text: e.target.value });
  const handleToggleActive = (e) =>
    setEditData({ ...editData, is_active: e.target.checked });

  return (
    <Box>
      <Box sx={{ background: "#04343a", p: "12px 20px" }}>
        <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>
          List of Disclaimers
        </Typography>
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{ borderTop: "1px solid #eef2f7" }}>
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              {[
                { label: "ID", width: "5%" },
                { label: "Disclaimer Text", width: "45%" },
                { label: "Active", width: "8%" },
                { label: "Created At", width: "16%" },
                { label: "Updated At", width: "16%" },
                { label: "Actions", width: "10%" },
              ].map(({ label, width }) => (
                <TableCell
                  key={label}
                  sx={{
                    fontWeight: 700,
                    fontSize: "13px",
                    color: "#fff",
                    background: "#04343a",
                    borderBottom: "none",
                    padding: "12px 16px",
                    width,
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#94a3b8" }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : disclaimers.length > 0 ? (
              disclaimers.map((item) => (
                <TableRow key={item.id} sx={{ "&:hover": { background: "#f0faf5" } }}>
                  <TableCell sx={{ color: "#64748b", fontSize: "13px" }}>{item.id}</TableCell>
                  <TableCell>
                    <Tooltip title={item.disclaimer_text} placement="top" arrow>
                      <Typography sx={{ fontWeight: 600, color: "#016551", fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {item.disclaimer_text}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={item.is_active}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#016551" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#016551" },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>
                    {new Date(item.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ color: "#64748b", fontSize: "12px" }}>
                    {new Date(item.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" sx={{ color: "#04343a" }} onClick={() => handleEdit(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: "#e53935" }} onClick={() => handleDelete(item.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#94a3b8" }}>
                  No disclaimers found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      {editData && (
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle
            sx={{ background: "#04343a", color: "#fff", fontWeight: 700, fontSize: "18px", pb: 2 }}
          >
            Edit Disclaimer
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Disclaimer Text"
              type="text"
              fullWidth
              value={editData.disclaimer_text}
              onChange={handleChangeText}
            />
            <Box mt={2} display="flex" alignItems="center" gap={1}>
              <Typography sx={{ fontSize: "14px", color: "#64748b" }}>Active:</Typography>
              <Switch
                checked={editData.is_active}
                onChange={handleToggleActive}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#016551" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#016551" },
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseDialog} sx={{ color: "#64748b", fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                background: "linear-gradient(135deg, #016551, #014d3d)",
                borderRadius: "8px",
                fontWeight: 600,
                px: 4,
                "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)" },
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default OptionsDisclaimerTable;
